import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Button, Select } from '@shared/components';

interface PaginationProps {
    currentPage: number;
    lastPage: number;
    total: number;
    perPage: number;
    onPageChange: (page: number) => void;
    onPerPageChange?: (perPage: number) => void;
    from?: number;
    to?: number;
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    lastPage,
    total,
    perPage,
    onPageChange,
    onPerPageChange,
    from,
    to,
}) => {
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;

        if (lastPage <= maxPagesToShow) {
            // Show all pages if total pages is less than maxPagesToShow
            for (let i = 1; i <= lastPage; i++) {
                pages.push(i);
            }
        } else {
            // Show first page
            pages.push(1);

            // Calculate start and end for middle pages
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(lastPage - 1, currentPage + 1);

            // Adjust if we're near the beginning
            if (currentPage <= 3) {
                end = 4;
            }

            // Adjust if we're near the end
            if (currentPage >= lastPage - 2) {
                start = lastPage - 3;
            }

            // Add ellipsis if there's a gap
            if (start > 2) {
                pages.push('...');
            }

            // Add middle pages
            for (let i = start; i <= end; i++) {
                if (i > 1 && i < lastPage) {
                    pages.push(i);
                }
            }

            // Add ellipsis if there's a gap
            if (end < lastPage - 1) {
                pages.push('...');
            }

            // Show last page
            if (lastPage > 1) {
                pages.push(lastPage);
            }
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div className="flex items-center justify-between px-4 py-3 bg-mp-surface border-t border-mp-border">
            {/* Results info */}
            <div className="flex items-center text-sm text-mp-muted">
                <span>
                    Showing {from || (currentPage - 1) * perPage + 1} to{' '}
                    {to || Math.min(currentPage * perPage, total)} of {total} results
                </span>

                {/* Per page selector */}
                {onPerPageChange && (
                    <div className="ml-4 flex items-center space-x-2">
                        <span>Show:</span>
                        <Select
                            value={perPage.toString()}
                            onChange={(e) => onPerPageChange(e as unknown as number)}
                            placeholder="Items per page"
                            options={[
                                { label: '10', value: '10' },
                                { label: '20', value: '20' },
                                { label: '50', value: '50' },
                                { label: '100', value: '100' },
                            ]}
                            disabled={total < perPage}
                            size="xs"
                        />
                        <span>per page</span>
                    </div>
                )}
            </div>

            {/* Pagination controls */}
            <div className="flex items-center space-x-1">
                {/* Previous button */}
                <Button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="xs"
                    className="flex items-center space-x-1"
                >
                    <ChevronLeftIcon size={16} />
                    <span>Previous</span>
                </Button>

                {/* Page numbers */}
                <div className="flex items-center space-x-1">
                    {pageNumbers.map((page, index) => (
                        <React.Fragment key={index}>
                            {page === '...' ? (
                                <span className="px-2 py-1 text-mp-muted">...</span>
                            ) : (
                                <Button
                                    onClick={() => onPageChange(page as number)}
                                    variant={currentPage === page ? 'primary' : 'outline'}
                                    size="xs"
                                    className="min-w-[1.5rem]"
                                >
                                    {page}
                                </Button>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Next button */}
                <Button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === lastPage}
                    variant="outline"
                    size="xs"
                    className="flex items-center space-x-1"
                >
                    <span>Next</span>
                    <ChevronRightIcon size={16} />
                </Button>
            </div>
        </div>
    );
};
